/**
 * Always use this util when using dates
 * Or use the shared date-format pipe for Angular templates
 *
 * Note that you MUST provide the locale when using utils
 * directly without using the pipe
 *
 * @example
 * ```ts
 *   import { formatDateLong, toDate } from '@shared/utils/date.utils'
 *   return formatDateLong(toDate(lastUpdatedPassword), this.#languageService.locale());
 * ```
 */

import type { ValueOf } from '@evc/web-components';

// Time constants in milliseconds
export const TIME = {
  SECOND: 1000,
  MINUTE: 60 * 1000,
  HOUR: 60 * 60 * 1000,
  DAY: 24 * 60 * 60 * 1000,
  WEEK: 7 * 24 * 60 * 60 * 1000,
} as const;

// Date unit constants
export const DATE_UNITS = {
  DAYS: 'days',
  MONTHS: 'months',
  YEARS: 'years',
  HOURS: 'hours',
  MINUTES: 'minutes',
  SECONDS: 'seconds',
} as const;

// Type for date units
export type DateUnit =
  | (typeof DATE_UNITS)[keyof typeof DATE_UNITS]
  | 'day'
  | 'month'
  | 'year'
  | 'hour'
  | 'minute'
  | 'second';

// Date format constants
export const DATE_FORMATS = {
  DateWithTime: 'MMM D, YYYY h:mm A',
  DateLong: 'MMMM D, YYYY',
  DateShort: 'MM/DD/YYYY',
  Time: 'h:mm A',
} as const;

// Type for date formats
export type DateFormat = ValueOf<typeof DATE_FORMATS>;

/**
 * Convert any date input to a Date object
 * @param date Input date (Date object, ISO string, or timestamp)
 * @returns JavaScript Date object
 */
export function toDate(date: Date | string | number): Date {
  if (date instanceof Date) {
    return date;
  }

  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return new Date(`${date}T00:00:00`);
  }

  return new Date(date);
}

/** Format a date relative to now using the requested locale. */
export function formatRelativeTime(
  date: Date | string | number,
  locale: string,
  now = new Date(),
): string {
  const dateObj = toDate(date);
  const elapsedMilliseconds = now.getTime() - dateObj.getTime();

  if (elapsedMilliseconds >= 0 && elapsedMilliseconds < TIME.MINUTE) {
    return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(0, 'second');
  }

  const timeUnits: { unit: Intl.RelativeTimeFormatUnit; count: number }[] = [
    { unit: 'year', count: now.getFullYear() - dateObj.getFullYear() },
    {
      unit: 'month',
      count:
        elapsedMilliseconds >= 4 * TIME.WEEK ? now.getMonth() - dateObj.getMonth() : 0,
    },
    { unit: 'week', count: Math.floor(elapsedMilliseconds / TIME.WEEK) },
    { unit: 'day', count: Math.floor(elapsedMilliseconds / TIME.DAY) },
    { unit: 'hour', count: Math.floor(elapsedMilliseconds / TIME.HOUR) },
    { unit: 'minute', count: Math.floor(elapsedMilliseconds / TIME.MINUTE) },
    { unit: 'second', count: Math.floor(elapsedMilliseconds / TIME.SECOND) },
  ];
  const timeUnit = timeUnits.find(({ count }) => count > 0);

  return timeUnit
    ? new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
        -timeUnit.count,
        timeUnit.unit,
      )
    : dateObj.toLocaleString(locale);
}

/**
 * Format a date according to the specified format string
 * @param date Date to format
 * @param formatStr Format string
 * @param locale Locale to use for formatting
 * @param utc Whether to use UTC time
 * @returns Formatted date string
 */
export function format(
  date: Date | string | number,
  locale: string,
  formatStr: DateFormat = DATE_FORMATS.DateWithTime,
  utc = false,
): string {
  if (!date) {
    return '';
  }

  const dateObj = toDate(date);

  // Handle common format patterns
  switch (formatStr) {
    case DATE_FORMATS.DateWithTime:
      return formatDateWithTime(dateObj, locale, utc);
    case DATE_FORMATS.DateLong:
      return formatDateLong(dateObj, locale, utc);
    case DATE_FORMATS.DateShort:
      return formatDateShort(dateObj, locale, utc);
    case DATE_FORMATS.Time:
      return formatTime(dateObj, locale, utc);
    default:
      return formatDateWithTime(dateObj, locale, utc);
  }
}

/**
 * Check if a date is before another date
 * @param date1 First date
 * @param date2 Second date
 * @returns True if date1 is before date2
 */
export function isBefore(date1: Date | string | number, date2: Date | string | number): boolean {
  const d1 = toDate(date1);
  const d2 = toDate(date2);

  return d1 < d2;
}

/**
 * Add a specified amount of time to a date
 * @param date Base date
 * @param amount Amount to add
 * @param unit Unit of time from DATE_UNITS
 * @returns New Date with the added time
 */
export function add(date: Date | string | number, amount: number, unit: DateUnit): Date {
  const d = toDate(date);
  const newDate = new Date(d);

  switch (unit) {
    case DATE_UNITS.DAYS:
    case 'day':
      newDate.setDate(d.getDate() + amount);
      break;
    case DATE_UNITS.MONTHS:
    case 'month':
      newDate.setMonth(d.getMonth() + amount);
      break;
    case DATE_UNITS.YEARS:
    case 'year':
      newDate.setFullYear(d.getFullYear() + amount);
      break;
    case DATE_UNITS.HOURS:
    case 'hour':
      newDate.setHours(d.getHours() + amount);
      break;
    case DATE_UNITS.MINUTES:
    case 'minute':
      newDate.setMinutes(d.getMinutes() + amount);
      break;
    case DATE_UNITS.SECONDS:
    case 'second':
      newDate.setSeconds(d.getSeconds() + amount);
      break;
    default:
      break;
  }

  return newDate;
}

/**
 * Set the date to the start of a specified unit of time
 * @param date Input date
 * @param unit Unit of time from DATE_UNITS
 * @returns New Date set to the start of the specified unit
 */
export function startOf(date: Date | string | number, unit: DateUnit): Date {
  const d = toDate(date);
  const newDate = new Date(d);

  switch (unit) {
    case DATE_UNITS.DAYS:
    case 'day':
      newDate.setHours(0, 0, 0, 0);
      break;
    case DATE_UNITS.MONTHS:
    case 'month':
      newDate.setDate(1);
      newDate.setHours(0, 0, 0, 0);
      break;
    case DATE_UNITS.YEARS:
    case 'year':
      newDate.setMonth(0, 1);
      newDate.setHours(0, 0, 0, 0);
      break;
    default:
      break;
  }

  return newDate;
}

/**
 * Convert a date to ISO string
 * @param date Input date
 * @returns ISO string representation of the date
 */
export function toISOString(date: Date | string | number): string {
  return toDate(date).toISOString();
}

/**
 * Format a date with time in the format 'MMM D, YYYY h:mm A'
 * @param date Date to format
 * @param locale Locale to use
 * @param utc Whether to use UTC time
 * @returns Formatted date string
 */
export function formatDateWithTime(date: Date, locale: string, utc = false): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: utc ? 'UTC' : undefined,
  }).format(date);
}

/**
 * Format a date in long format 'MMMM D, YYYY'
 * @param date Date to format
 * @param locale Locale to use
 * @param utc Whether to use UTC time
 * @returns Formatted date string
 */
export function formatDateLong(date: Date, locale: string, utc = false): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: utc ? 'UTC' : undefined,
  }).format(date);
}

/**
 * Format a date in short format 'MM/DD/YYYY'
 * @param date Date to format
 * @param locale Locale to use
 * @param utc Whether to use UTC time
 * @returns Formatted date string
 */
export function formatDateShort(date: Date, locale: string, utc = false): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: utc ? 'UTC' : undefined,
  }).format(date);
}

/**
 * Format just the time portion of a date 'h:mm A'
 * @param date Date to format
 * @param locale Locale to use
 * @param utc Whether to use UTC time
 * @returns Formatted time string
 */
export function formatTime(date: Date, locale: string, utc = false): string {
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: utc ? 'UTC' : undefined,
  }).format(date);
}

/**
 * Format a date in ISO date format (YYYY-MM-DD)
 * @param date Date to format
 * @param utc Whether to use UTC time
 * @returns Formatted date string
 */
export function formatISODate(date: Date, utc = false): string {
  const year = utc ? date.getUTCFullYear() : date.getFullYear();
  const month = utc ? date.getUTCMonth() + 1 : date.getMonth() + 1;
  const day = utc ? date.getUTCDate() : date.getDate();

  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Format a date in ISO date-time format (YYYY-MM-DD HH:mm:ss)
 * @param date Date to format
 * @param utc Whether to use UTC time
 * @returns Formatted date-time string
 */
export function formatISODateTime(date: Date, utc = false): string {
  const hours = String(utc ? date.getUTCHours() : date.getHours()).padStart(2, '0');
  const minutes = String(utc ? date.getUTCMinutes() : date.getMinutes()).padStart(2, '0');
  const seconds = String(utc ? date.getUTCSeconds() : date.getSeconds()).padStart(2, '0');

  return `${formatISODate(date)} ${hours}:${minutes}:${seconds}`;
}
